import React, { useState } from 'react';
import { X, AlertTriangle, CheckCircle, XCircle, Loader } from 'lucide-react';
import './BulkConfirmModal.css';

/**
 * Modal di conferma per azioni massive
 * 
 * @param {Object} props
 * @param {Boolean} props.isOpen - Se il modal è aperto
 * @param {Function} props.onClose - Callback per chiudere
 * @param {Function} props.onConfirm - Callback per confermare (async)
 * @param {String} props.title - Titolo modal
 * @param {String} props.message - Messaggio descrittivo
 * @param {String} props.actionLabel - Label bottone azione (es: "Sospendi")
 * @param {String} props.actionColor - Colore bottone (red, green, orange, blue)
 * @param {Number} props.selectedCount - Numero record selezionati
 * @param {Array} props.items - Array di oggetti da mostrare nel riepilogo
 * @param {String} props.itemLabel - Label per singolo item (es: "abbonamento")
 * @param {String} props.warningText - Testo warning opzionale
 */
const BulkConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  actionLabel = 'Conferma',
  actionColor = 'blue',
  selectedCount,
  items = [],
  itemLabel = 'elemento',
  warningText
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success: [], failed: [] }

  if (!isOpen) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const result = await onConfirm();
      
      // Se l'azione ritorna un risultato con successi/fallimenti
      if (result && (result.success || result.failed)) {
        setResult(result);
      } else {
        // Altrimenti chiudi direttamente
        onClose();
      }
    } catch (error) {
      console.error('Errore azione massiva:', error);
      setResult({
        success: [],
        failed: items.map(item => ({
          id: item.id,
          error: error.message || 'Errore sconosciuto'
        }))
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  // Se abbiamo un risultato, mostra il riepilogo
  if (result) {
    const successCount = result.success?.length || 0;
    const failedCount = result.failed?.length || 0;
    const totalCount = successCount + failedCount;

    return (
      <div className="bulk-modal-overlay" onClick={handleClose}>
        <div className="bulk-modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bulk-modal-header">
            <h2 className="bulk-modal-title">Operazione Completata</h2>
            <button onClick={handleClose} className="bulk-modal-close">
              <X />
            </button>
          </div>

          {/* Result Summary */}
          <div className="bulk-modal-body">
            <div className="bulk-result-summary">
              {successCount > 0 && (
                <div className="bulk-result-item bulk-result-success">
                  <CheckCircle className="bulk-result-icon" />
                  <div>
                    <p className="bulk-result-count">{successCount} su {totalCount}</p>
                    <p className="bulk-result-text">Operazione riuscita</p>
                  </div>
                </div>
              )}
              
              {failedCount > 0 && (
                <div className="bulk-result-item bulk-result-error">
                  <XCircle className="bulk-result-icon" />
                  <div>
                    <p className="bulk-result-count">{failedCount} su {totalCount}</p>
                    <p className="bulk-result-text">Operazione fallita</p>
                  </div>
                </div>
              )}
            </div>

            {/* Failed Items Details */}
            {failedCount > 0 && (
              <div className="bulk-failed-details">
                <h4 className="bulk-failed-title">Dettagli Errori:</h4>
                <ul className="bulk-failed-list">
                  {result.failed.map((item, idx) => (
                    <li key={idx} className="bulk-failed-item">
                      <span className="bulk-failed-id">{item.name || item.id}</span>
                      <span className="bulk-failed-error">{item.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bulk-modal-footer">
            <button onClick={handleClose} className="bulk-modal-btn bulk-modal-btn-primary">
              Chiudi
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Modal di conferma normale
  return (
    <div className="bulk-modal-overlay" onClick={onClose}>
      <div className="bulk-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bulk-modal-header">
          <h2 className="bulk-modal-title">{title}</h2>
          <button onClick={onClose} className="bulk-modal-close" disabled={loading}>
            <X />
          </button>
        </div>

        {/* Body */}
        <div className="bulk-modal-body">
          {/* Warning Icon */}
          <div className="bulk-modal-icon-wrapper">
            <AlertTriangle className="bulk-modal-icon" />
          </div>

          {/* Message */}
          <p className="bulk-modal-message">{message}</p>

          {/* Count Badge */}
          <div className="bulk-count-badge">
            <strong>{selectedCount}</strong> {selectedCount === 1 ? itemLabel : `${itemLabel}i`}
          </div>

          {/* Warning Text */}
          {warningText && (
            <div className="bulk-warning-box">
              <AlertTriangle className="bulk-warning-icon" />
              <p>{warningText}</p>
            </div>
          )}

          {/* Items Preview (max 5) */}
          {items.length > 0 && (
            <div className="bulk-items-preview">
              <h4 className="bulk-preview-title">Elementi interessati:</h4>
              <ul className="bulk-preview-list">
                {items.slice(0, 5).map((item, idx) => (
                  <li key={idx} className="bulk-preview-item">
                    {item.name || item.codice || item.id}
                  </li>
                ))}
                {items.length > 5 && (
                  <li className="bulk-preview-more">
                    ... e altri {items.length - 5} {itemLabel}{items.length - 5 !== 1 && 'i'}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bulk-modal-footer">
          <button
            onClick={onClose}
            className="bulk-modal-btn bulk-modal-btn-secondary"
            disabled={loading}
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            className={`bulk-modal-btn bulk-modal-btn-${actionColor}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="bulk-spinner" />
                Elaborazione...
              </>
            ) : (
              actionLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkConfirmModal;
